import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { getCombatState, ensureEnemyImage } from "@/lib/game/combat.functions";
import { setZoneTarget, lifespark, regenerateMonsterAppearance } from "@/lib/game/monster.functions";
import { listItems } from "@/lib/game/items.functions";
import { useConsumable } from "@/lib/game/shop.functions";
import {
  type AutoConfig,
  type AutoKind,
  type AutoSlot,
  DEFAULT_AUTO_CONFIG,
  migrateAutoConfig,
  isHeal,
  pickByStrategy,
} from "@/lib/game/items-classify";
import { markSelfMutation } from "@/lib/game/self-tick-marker";
import { StatBar } from "@/components/game/StatBar";
import { EventFeed, COMBAT_TYPES, COMBAT_FILTERS } from "@/components/game/EventFeed";
import { EnemyCard } from "@/components/game/EnemyCard";
import { CombatLog } from "@/components/game/CombatLog";
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Zap, Swords, Sparkles,
  Coins, Brain, Loader2, Moon, Minimize2, Maximize2,
} from "lucide-react";

import { InlineItemAction } from "@/components/game/InlineItemAction";
import { InfoTip } from "@/components/game/InfoTip";
import { MovesPanel } from "@/components/game/MovesPanel";
import { useTutorial, useTutorialCtx } from "@/components/game/TutorialProvider";
import { PageSkeleton } from "@/components/game/PageSkeleton";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";

import { ConsumablesQuickbar } from "@/components/game/ConsumablesQuickbar";
import { XenomonSpeechBubble } from "@/components/game/XenomonSpeechBubble";
import { PlayerHitFx } from "@/components/game/PlayerHitFx";
import { PlayerAttackTimer } from "@/components/game/PlayerAttackTimer";
// UltimateFlash wurde durch globales MoveCastFlash (in GameShell) ersetzt.
import { IdleStanceSelect, type IdleStance } from "@/components/game/IdleStanceSelect";
import { IdleReport, type IdleReportData, type IdleSummary } from "@/components/game/IdleReport";
import type { Mood } from "@/lib/game/dialogue";
import { useCombatCues } from "@/lib/feedback/combat-cues";
import { playCue } from "@/lib/feedback/sound";
import { vibrate } from "@/lib/feedback/haptic";




import { toast } from "sonner";
import { typeLabel, xpForLevel, type CreatureType } from "@/lib/game/types";
import { tServerError } from "@/lib/i18n/server-error";

// Spacer in der gleichen Breite wie ein InlineItemAction-Block (2x w-7 + gap-1 + ml-2),
// damit alle StatBars rechts bündig enden — auch ohne Aktionsknöpfe.
function ActionSpacer() {
  return (
    <div aria-hidden className="ml-2 flex shrink-0 items-center gap-1 invisible">
      <div className="h-7 min-w-[2.25rem]" />
      <div className="h-7 w-7" />
    </div>
  );
}

// Modul-scope: stabile Komponenten-Identität, damit Radix-Popover-State
// (Auto-Heil/-Futter/-Energie/-Pflege) Combat-Ticks übersteht.
function StatBarRow({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">{children}</div>
      {action ?? <ActionSpacer />}
    </div>
  );
}

export const Route = createFileRoute("/_app/game/home")({
  component: HomePage,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

function HomePage() {
  const qc = useQueryClient();
  const { t } = useTranslation("game");
  useTutorial("home_intro");
  useTutorial("zone_coach");
  useTutorial("nav_moves_coach");
  const { trigger: triggerTutorial } = useTutorialCtx();
  const navigate = useNavigate();
  const fetchState = useServerFn(getCombatState);
  // Combat-Tick läuft global im BackgroundCombatTick (siehe useEffect unten).
  const setZoneFn = useServerFn(setZoneTarget);
  const spark = useServerFn(lifespark);
  const fetchItems = useServerFn(listItems);
  const ensureImg = useServerFn(ensureEnemyImage);
  const regenAppearance = useServerFn(regenerateMonsterAppearance);


  const { data, isLoading } = useQuery({
    queryKey: ["combat-state"],
    queryFn: () => fetchState(),
    refetchOnWindowFocus: true,
    staleTime: 4_000,
  });

  const { data: itemsData } = useQuery({
    queryKey: ["items"],
    queryFn: () => fetchItems(),
    staleTime: 30_000,
  });

  // Combat-Sound + Haptik bei neuen Log-Einträgen
  const combatLog = (data as { state?: { log?: { kind: string; text: string; detail?: string }[] } } | undefined)?.state?.log;
  useCombatCues(combatLog);

  // Lokale Uhr für Care-Cooldowns & Effekt-Restzeiten. 250 ms ergibt glatte
  // Fortschritts-Balken ohne spürbar mehr Re-Renders (HomePage rendert ohnehin
  // bei jedem Combat-Tick neu).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, []);


  // Auto-Use Konfiguration pro Monster (persistiert in localStorage).
  const [autoConfig, setAutoConfig] = useState<AutoConfig>(DEFAULT_AUTO_CONFIG);
  const autoMonsterIdRef = useRef<string | null>(null);
  const autoInflightRef = useRef<boolean>(false);

  // Kompakter Kampf-View (umschaltbar, pro Monster in localStorage persistiert).
  const [compactView, setCompactView] = useState<boolean>(false);
  const [statsOpen, setStatsOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("xeno-combat-stats-open") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("xeno-combat-stats-open", statsOpen ? "1" : "0"); } catch { /* ignore */ }
  }, [statsOpen]);


  

  // Auto-Heal: nur für frisch geschlüpfte Monster (pending_ai) — fehlendes
  // image_url allein triggert NIE eine Neugenerierung. Manueller Button
  // setzt `manual: true`.
  const regenAttemptedRef = useRef<Set<string>>(new Set());
  const regenAppearanceMut = useMutation({
    mutationFn: (vars: { monsterId: string; manual: boolean }) =>
      regenAppearance({ data: { monsterId: vars.monsterId, manual: vars.manual } }),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ["combat-state"] });
      qc.invalidateQueries({ queryKey: ["my-monster"] });
      qc.invalidateQueries({ queryKey: ["my-monsters"] });
      if (vars.manual) toast.success(t("home.toastAppearanceGenerated"));
    },
    onError: (e: Error, vars) => {
      if (vars.manual) toast.error(t("home.toastError", { message: e.message }));
    },
  });


  const [combatMood, setCombatMood] = useState<Mood | undefined>(undefined);
  const [idleReport, setIdleReport] = useState<IdleReportData | null>(null);
  const [activeBonuses, setActiveBonuses] = useState<{ live: boolean; topForm: boolean }>({ live: false, topForm: false });

  const moodTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  function flashMood(mood: Mood) {
    if (moodTimerRef.current) clearTimeout(moodTimerRef.current);
    setCombatMood(mood);
    moodTimerRef.current = setTimeout(() => setCombatMood(undefined), 6500);
  }
  // Der globale TickCoordinator (BackgroundCombatTick) tickert für uns mit und
  // dispatcht jedes Ergebnis als `combat-tick` Window-Event. Hier hängen wir
  // die Home-spezifischen UI-Reaktionen (Mood, Toasts, Idle-Report, Tutorials,
  // Bonus-Banner, Cast-Events) an dieses Event statt einer zweiten Tick-Schleife.
  useEffect(() => {
    function onTick(ev: Event) {
      const res = (ev as CustomEvent<import("@/components/game/BackgroundCombatTick").CombatTickDetail & { firstTick: boolean; offlineSeconds: number }>).detail;
      if (!res) return;

      // Cast-Events: solange der Operator im Kampf-View ist, zeigen wir Auto-Cast
      // Animationen genauso wie manuelle Casts. Bei längeren Offline-Ticks bleibt
      // es still, damit nach dem Reinkommen nicht ein Schwall an Flashes läuft.
      const castEvents = res.cast_events;
      if (castEvents && castEvents.length > 0 && !res.firstTick) {
        if ((res.offlineSeconds ?? 0) < 30) {
          import("@/lib/game/cast-events").then((m) => m.handleTickCastEvents(castEvents, { showFlash: true }));
        }
      }

      const gains = res.gains ?? {};
      const bon = res.bonuses;
      if (bon) setActiveBonuses({ live: !!bon.live, topForm: !!bon.topForm });

      const killCount = gains.kills ?? 0;
      const defeatCount = gains.defeats ?? 0;
      const offlineSec = res.offlineSeconds ?? 0;

      if (killCount > 0) {
        triggerTutorial("first_battle_won");
        flashMood("victorious");
        if (!res.firstTick && offlineSec < 60) {
          const xp = gains.xp ?? 0;
          const gold = gains.gold ?? 0;
          toast.success(
            killCount === 1
              ? t("home.toastVictorySingle", { xp, gold })
              : t("home.toastVictoryMulti", { count: killCount, xp, gold }),
            { duration: 2200 },
          );
        }
      }
      if (defeatCount > 0 && !res.firstTick && offlineSec < 60) {
        toast.error(
          defeatCount === 1
            ? t("home.toastDefeatSingle")
            : t("home.toastDefeatMulti", { count: defeatCount }),
          { duration: 2400 },
        );
      }

      if (gains.leveledUp) {
        triggerTutorial("level_up_intro");
        flashMood("level_up");
        playCue("levelup");
        vibrate("levelup");
      }
      if ((gains.loot ?? 0) > 0) {
        triggerTutorial("first_loot_coach");
        playCue("loot");
        vibrate("loot");
      }
      if ((gains.eggs ?? 0) > 0) {
        triggerTutorial("first_egg_coach");
        playCue("egg");
      }

      if (offlineSec > 120 && res.firstTick) {
        setIdleReport({
          offlineSeconds: offlineSec,
          gains: {
            xp: gains.xp ?? 0,
            gold: gains.gold ?? 0,
            loot: gains.loot ?? 0,
            eggs: gains.eggs ?? 0,
            kills: gains.kills,
            leveledUp: gains.leveledUp,
          },
          summary: (res.idleSummary as IdleSummary | undefined) ?? {
            specialsFired: 0, ultimatesFired: 0, masteryUps: 0, maxCombo: 0, bestCrit: 0,
          },
        });
      } else if (offlineSec > 60 && res.firstTick) {
        const min = Math.floor(offlineSec / 60);
        toast.success(t("home.toastWelcomeBack", { min, xp: gains.xp ?? 0, gold: gains.gold ?? 0 }) + (gains.loot ? `, ${gains.loot} Loot` : ""));
      }
    }
    window.addEventListener("combat-tick", onTick);
    return () => window.removeEventListener("combat-tick", onTick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  useEffect(() => {
    if (isLoading || !data) return;
    if (!data.monster) {
      navigate({ to: "/game/onboarding", replace: true });
    }
  }, [data, isLoading, navigate]);





  // Auto-Bildgenerierung für neuen Gegner (idempotent, einmalig pro slug)
  const triedImageFor = useRef<string | null>(null);
  const enemyForImg = data?.state?.enemy as { slug?: string; image_url?: string | null } | undefined;
  useEffect(() => {
    const slug = enemyForImg?.slug;
    if (!slug || enemyForImg?.image_url) return;
    if (triedImageFor.current === slug) return;
    triedImageFor.current = slug;
    ensureImg()
      .then((r) => {
        if (r?.ok) qc.invalidateQueries({ queryKey: ["combat-state"] });
      })
      .catch(() => { /* still no image, retry next spawn */ });
  }, [enemyForImg?.slug, enemyForImg?.image_url, ensureImg, qc]);

  // Auto-Bildgenerierung (pending_ai) läuft seit dem Onboarding-Redirect
  // global in MonsterLifecycle (GameShell) — hier bleibt nur der manuelle
  // Regen-Button (regenAppearanceMut mit manual:true).


  const sparkMut = useMutation({
    mutationFn: () => spark(),
    onSuccess: (res) => {
      toast.success(t("home.toastLifesparkHealed"));
      const r = res as { monster?: Record<string, unknown> } | undefined;
      if (r?.monster) {
        markSelfMutation();
        qc.setQueryData(["combat-state"], (old: unknown) => {
          const o = old as { monster?: Record<string, unknown> | null; state?: unknown } | undefined;
          return o ? { ...o, monster: { ...(o.monster ?? {}), ...r.monster } } : old;
        });
      }
      qc.invalidateQueries({ queryKey: ["events"] });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  // Lokales Zone-Target (Pfeile sind spammbar) + debouncter Server-Call.
  const [zoneTarget, setZoneTarget_] = useState<number | null>(null);
  const zoneDebounceRef = useRef<number | null>(null);
  const zoneMut = useMutation({
    mutationFn: (target: number) => setZoneFn({ data: { targetZone: target } }),
    onSuccess: (res) => {
      const r = res as { monster: { zone: number; pending_zone: number | null } };
      qc.setQueryData(["combat-state"], (old: unknown) => {
        const o = old as { monster: unknown; state: unknown } | undefined;
        return o ? { ...o, monster: { ...(o.monster as object), ...r.monster } } : o;
      });
      setZoneTarget_(null);
    },
    onError: (e: Error) => {
      toast.error(tServerError(e));
      setZoneTarget_(null);
    },
  });
  const bumpZone = (delta: number) => {
    const m = data?.monster as { zone: number; pending_zone?: number | null } | undefined;
    if (!m) return;
    const base = zoneTarget ?? m.pending_zone ?? m.zone;
    const next = Math.max(1, base + delta);
    setZoneTarget_(next);
    if (zoneDebounceRef.current) window.clearTimeout(zoneDebounceRef.current);
    zoneDebounceRef.current = window.setTimeout(() => {
      zoneMut.mutate(next);
    }, 280);
  };

  // ─── Auto-Use: localStorage laden/speichern pro Monster ──────────────────
  // Hooks müssen vor dem early-return stehen — daher hier mit optional access.
  const monId = (data?.monster as { id?: string } | null | undefined)?.id ?? null;
  useEffect(() => {
    if (!monId || autoMonsterIdRef.current === monId) return;
    autoMonsterIdRef.current = monId;
    try {
      const raw = localStorage.getItem(`xeno-auto-care:${monId}`);
      setAutoConfig(migrateAutoConfig(raw ? JSON.parse(raw) : null));
    } catch {
      setAutoConfig(DEFAULT_AUTO_CONFIG);
    }
  }, [monId]);
  useEffect(() => {
    if (!monId) return;
    try { localStorage.setItem(`xeno-auto-care:${monId}`, JSON.stringify(autoConfig)); } catch { /* ignore */ }
  }, [autoConfig, monId]);

  // Kompakt-View aus localStorage laden (pro Monster).
  const compactMonsterIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!monId || compactMonsterIdRef.current === monId) return;
    compactMonsterIdRef.current = monId;
    const mobileDefault = typeof window !== "undefined" && window.innerWidth < 640;
    try {
      const raw = localStorage.getItem(`xeno-combat-compact:${monId}`);
      if (raw === "1") setCompactView(true);
      else if (raw === "0") setCompactView(false);
      else setCompactView(mobileDefault);
    } catch {
      setCompactView(mobileDefault);
    }
  }, [monId]);
  useEffect(() => {
    if (!monId) return;
    try { localStorage.setItem(`xeno-combat-compact:${monId}`, compactView ? "1" : "0"); } catch { /* ignore */ }
  }, [compactView, monId]);


  const consumeItemFn = useServerFn(useConsumable);
  const consumeMut = useMutation({
    mutationFn: (itemId: string) => consumeItemFn({ data: { itemId } }),
    // Optimistisch: Item-Effekte (item.stats) sofort auf das Monster anwenden,
    // damit HP-/Hunger-/Energie-Bars unmittelbar reagieren. onSuccess ersetzt
    // den Snapshot durch den autoritativen Stand aus der Server-Antwort.
    onMutate: async (itemId) => {
      await qc.cancelQueries({ queryKey: ["combat-state"] });
      const previous = qc.getQueryData(["combat-state"]);
      const itemsArr = (itemsData?.items ?? []) as Array<{ id: string; stats?: Record<string, number> | null }>;
      const item = itemsArr.find((it) => it.id === itemId);
      if (item) {
        qc.setQueryData(["combat-state"], (old: unknown) => {
          const o = old as { monster?: Record<string, unknown> | null } | undefined;
          if (!o?.monster) return old;
          const m = o.monster as Record<string, unknown>;
          // Nur noch HP-Consumables: item.stats.hp direkt auf HP anwenden.
          const stats = (item.stats ?? {}) as Record<string, number>;
          const hpDelta = Number(stats.hp ?? 0);
          if (!hpDelta) return old;
          const hp = Number(m.hp ?? 0);
          const hpMax = Number(m.hp_max ?? 0);
          const nextHp = hpMax > 0 ? Math.min(hpMax, hp + hpDelta) : hp + hpDelta;
          return { ...o, monster: { ...m, hp: nextHp } };
        });
      }
      markSelfMutation();
      return { previous };
    },
    onError: (e: Error, _id, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData(["combat-state"], ctx.previous);
      toast.error(tServerError(e));
    },
    onSuccess: (res) => {
      const r = res as { ok?: boolean; monster?: Record<string, unknown> } | undefined;
      if (r?.monster) {
        markSelfMutation();
        qc.setQueryData(["combat-state"], (old: unknown) => {
          const o = old as { monster?: Record<string, unknown> | null; state?: unknown } | undefined;
          return o ? { ...o, monster: { ...(o.monster ?? {}), ...r.monster } } : old;
        });
      }
      qc.invalidateQueries({ queryKey: ["items"] });
    },
  });

  // Auto-Use Tick: pro Tick maximal EIN useConsumable. Der Lock bleibt aktiv,
  // bis combat-state UND items frisch refetched sind — sonst würde der nächste
  // 1s-Tick mit veralteten HP-Werten erneut feuern.
  useEffect(() => {
    const mon = data?.monster;
    if (!mon) return;
    if (autoInflightRef.current) return;
    const itemsArr = itemsData?.items ?? [];

    // Nur noch Auto-Heal (HP-Consumables). Andere Pflege-Slots wurden entfernt.
    const slot = autoConfig.heal;
    if (!slot.enabled) return;
    const hpMax = Math.max(1, mon.hp_max);
    const pct = (mon.hp / hpMax) * 100;
    if (pct >= slot.threshold) return;
    const item = pickByStrategy(itemsArr.filter(isHeal), slot.prefer);
    if (!item) return;

    autoInflightRef.current = true;
    (async () => {
      try {
        await consumeMut.mutateAsync(item.id);
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["combat-state"], refetchType: "active" }),
          qc.invalidateQueries({ queryKey: ["items"], refetchType: "active" }),
        ]);
      } catch {
        // Fehler-Toast kommt aus consumeMut.onError.
      } finally {
        autoInflightRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowMs, autoConfig, data?.monster, itemsData?.items]);

  if (isLoading || !data?.monster) {
    return <PageSkeleton variant="home" />;
  }


  const m = data.monster as typeof data.monster & {
    rest_until: string | null; atk_buff_until: string | null;
    def_buff_until: string | null; crit_buff_until: string | null;
    stun_target_until: string | null; dot_until: string | null;
    lifesteal_until: string | null; accuracy_down_until: string | null;
    focus_battles_left: number;
  };
  const enemy = data.state?.enemy as {
    name: string;
    hp: number;
    hp_max: number;
    tier: string;
    image_url?: string | null;
    intent?: { name: string; ready_in: number; max?: number; ready_at?: number } | null;
    recent_specials?: { name: string; dmg: number }[];
    next_attack_at?: number;
  } | undefined;


  // Nur noch HP-Consumables (Pflege-Items existieren seit Stufe 3 nicht mehr).
  const items = itemsData?.items ?? [];

  // Lifespark: reine HP-Regeneration (Stufe 4e) — 50% max HP, Cooldown 48h, Level 20+.
  const lastSparkIso = (m as { last_lifespark_at?: string | null }).last_lifespark_at ?? null;
  const sparkRemainHr = lastSparkIso
    ? Math.max(0, Math.ceil((new Date(lastSparkIso).getTime() + 48 * 3600_000 - nowMs) / 3600_000))
    : 0;
  const sparkLevelOk = (m.level ?? 0) >= 20;
  const sparkAvailable = sparkLevelOk && sparkRemainHr === 0 && m.hp < m.hp_max;

  const restUntilMs = m.rest_until ? new Date(m.rest_until).getTime() : 0;
  const buffUntilMs = m.atk_buff_until ? new Date(m.atk_buff_until).getTime() : 0;
  const restingSec = Math.max(0, Math.ceil((restUntilMs - nowMs) / 1000));
  const buffSec = Math.max(0, Math.ceil((buffUntilMs - nowMs) / 1000));

  // Aktive Move-Effekte (Anzeige + Restzeit)
  const activeEffects: { key: string; label: string; sec: number; cls: string }[] = [];
  const pushEffect = (until: string | null, label: string, cls: string, key: string) => {
    if (!until) return;
    const sec = Math.max(0, Math.ceil((new Date(until).getTime() - nowMs) / 1000));
    if (sec > 0) activeEffects.push({ key, label, sec, cls });
  };
  pushEffect(m.def_buff_until, "DEF ×2", "border-sky-400/40 bg-sky-500/10 text-sky-300", "def");
  pushEffect(m.crit_buff_until, "+Crit", "border-amber-400/40 bg-amber-500/10 text-amber-300", "crit");
  pushEffect(m.lifesteal_until, "Lifesteal", "border-emerald-400/40 bg-emerald-500/10 text-emerald-300", "ls");
  pushEffect(m.dot_until, "DoT", "border-orange-400/40 bg-orange-500/10 text-orange-300", "dot");
  pushEffect(m.stun_target_until, t("home.effectEnemyStunned"), "border-yellow-400/40 bg-yellow-500/10 text-yellow-300", "stun");
  pushEffect(m.accuracy_down_until, t("home.effectEnemyAccDown"), "border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-300", "acc");

  // Empfohlenes Level für aktuelle Zone (grobe Heuristik passend zu makeEnemy)
  const recommendedLevel = Math.max(1, m.zone * 3);

  // Item-Auswahl: nur noch Heal-Slot.
  const healItem = pickByStrategy(items.filter(isHeal), autoConfig.heal.prefer);

  const setSlot = (key: AutoKind) => (next: AutoSlot) =>
    setAutoConfig((p) => ({ ...p, [key]: next }));




  const mz = m as typeof m & { pending_zone?: number | null };
  const displayedZoneTarget = zoneTarget ?? mz.pending_zone ?? null;
  const showPendingZone = displayedZoneTarget != null && displayedZoneTarget !== m.zone;

  return (
    <div className="relative min-h-screen">
      {/* MoveCastFlash ist global in GameShell montiert */}
      <IdleReport data={idleReport} onClose={() => setIdleReport(null)} />
      {/* EvolutionModal + EvolutionReadyBanner sind nach MonsterLifecycle
          (GameShell, alle /game-Routen) umgezogen. */}

      <div className="absolute inset-0 bg-grid opacity-20" aria-hidden />
      <div className="absolute inset-0 scanlines opacity-30 pointer-events-none" aria-hidden />

      <main className="relative z-10 mx-auto max-w-7xl px-3 pb-12 pt-4 sm:px-6 sm:pt-6">
        {/* ─── Topbar: kompakte Kampf-Kopfzeile ────────────────────────── */}
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-primary/30 bg-card/70 px-3 py-2 shadow-card backdrop-blur sm:flex sm:flex-wrap sm:justify-between sm:px-4 sm:py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate font-display text-base font-black text-glow sm:text-lg">{m.name}</h1>
            <span className="shrink-0 text-xs text-muted-foreground">Lv {m.level}</span>
            <span className="shrink-0 inline-block rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-primary">
              {typeLabel(((m as { type?: CreatureType }).type ?? "neon") as CreatureType)}
            </span>
            
          </div>
          <div className="flex shrink-0 items-center gap-3 text-xs">
            <IdleStanceSelect stance={((data?.monster as { idle_stance?: IdleStance } | null)?.idle_stance ?? "balance") as IdleStance} />
            <InfoTip text={compactView ? t("home.tipCompactActive") : t("home.tipStandardActive")}>
              <button
                onClick={() => setCompactView((v) => !v)}
                aria-label={compactView ? t("home.ariaStandardView") : t("home.ariaCompactView")}
                className="inline-flex h-7 w-7 items-center justify-center rounded border border-primary/30 hover:bg-primary/10"
              >
                {compactView ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
              </button>
            </InfoTip>
          </div>
        </header>

        {/* ─── Arena: Gegner zentriert + eigener Xeno + Moves ─────────── */}
        <div className={`mt-4 grid grid-cols-1 ${compactView ? "gap-2" : "gap-4"} lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]`}>
          <section className={`flex min-w-0 flex-col ${compactView ? "gap-2" : "gap-4"}`}>

            {/* Zone-Wechsler — prominente eigene Zeile über dem Gegner */}
            <InfoTip text={t("home.zoneTip", { level: recommendedLevel })}>
              <div className={`flex items-center justify-between rounded-2xl border border-primary/30 bg-card/70 shadow-card backdrop-blur ${compactView ? "px-3 py-2" : "px-4 py-3"}`}>
                <div className="flex min-w-0 items-center gap-2">
                  <Zap className={`shrink-0 ${compactView ? "h-4 w-4" : "h-5 w-5"} text-primary`} />
                  <div className="min-w-0">
                    <div className={`font-mono uppercase tracking-widest text-muted-foreground ${compactView ? "text-[10px]" : "text-xs"}`}>{t("home.zoneLabel")}</div>
                    {!compactView && (
                      <div className="truncate text-xs text-muted-foreground">
                        {t("home.zoneRecommendedLevel", { level: recommendedLevel })}
                        {showPendingZone && (
                          <span className="ml-2 text-amber-300/90">{t("home.zonePendingHint", { zone: displayedZoneTarget })}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => bumpZone(-1)}
                    disabled={(displayedZoneTarget ?? m.zone) <= 1}
                    aria-label={t("home.ariaZoneDown")}
                    className={`inline-flex items-center justify-center rounded-lg border border-primary/40 bg-background/60 text-primary hover:bg-primary/15 active:scale-95 disabled:opacity-40 transition ${compactView ? "h-8 w-8" : "h-10 w-10"}`}
                  >
                    <ChevronLeft className={compactView ? "h-4 w-4" : "h-5 w-5"} />
                  </button>
                  <div className="flex items-baseline gap-1 px-1 tabular-nums">
                    <span className={`font-display font-black text-primary text-glow ${compactView ? "text-lg" : "text-2xl"}`}>Z{m.zone}</span>
                    {showPendingZone && (
                      <span className={`font-mono text-amber-300/90 ${compactView ? "text-[10px]" : "text-xs"}`}>→{displayedZoneTarget}</span>
                    )}
                  </div>
                  <button
                    data-tutorial-target="zone-up"
                    onClick={() => bumpZone(1)}
                    aria-label={t("home.ariaZoneUp")}
                    className={`inline-flex items-center justify-center rounded-lg border border-primary/40 bg-background/60 text-primary hover:bg-primary/15 active:scale-95 transition ${compactView ? "h-8 w-8" : "h-10 w-10"}`}
                  >
                    <ChevronRight className={compactView ? "h-4 w-4" : "h-5 w-5"} />
                  </button>
                </div>
              </div>
            </InfoTip>

            {enemy && (
              <div className="order-2 sm:order-none">
                <EnemyCard enemy={enemy} playerType={m?.type as CreatureType | undefined} compact={compactView} />
              </div>
            )}

            {/* Eigener Xeno – kompakte Arena-Karte.
                Auf Mobile rückt die MovesPanel-Karte (Spezialattacken) davor,
                damit HP-Gegner, eigene HP und Attacken gleichzeitig sichtbar bleiben. */}
            <div className={`order-4 sm:order-none rounded-2xl border border-primary/30 bg-card/70 shadow-card backdrop-blur ${compactView ? "p-2.5" : "p-3 sm:p-4"}`}>
              <div className="flex items-start gap-3">
                <div className="relative shrink-0">
                  <XenomonSpeechBubble monster={m} moodOverride={combatMood} />
                  <PlayerHitFx hp={m.hp} hp_max={m.hp_max} resetKey={m.id}>
                    {m.image_url ? (
                      <img
                        src={m.image_url}
                        alt={m.name}
                        className={`rounded-xl object-cover animate-pulse-glow ${compactView ? "h-16 w-16 sm:h-20 sm:w-20" : "h-24 w-24 sm:h-28 sm:w-28"}`}
                      />
                    ) : (
                      <div className={`flex flex-col items-center justify-center gap-1 rounded-xl bg-gradient-to-br from-primary/40 to-accent/40 px-2 text-center ${compactView ? "h-16 w-16 sm:h-20 sm:w-20" : "h-24 w-24 sm:h-28 sm:w-28"}`}>

                        {(() => {
                          const loading = regenAppearanceMut.isPending || m.pending_ai;
                          if (loading) {
                            return <Loader2 className="h-7 w-7 animate-spin text-primary-foreground" />;
                          }
                          return (
                            <button
                              onClick={() => {
                                if (m.id) regenAttemptedRef.current.delete(m.id);
                                regenAppearanceMut.mutate({ monsterId: m.id, manual: true });
                              }}
                              className="rounded-md border border-primary/40 bg-background/60 px-1.5 py-1 text-[10px] font-medium text-primary hover:bg-primary/10"
                            >
                              {t("home.generateImage")}
                            </button>
                          );
                        })()}
                      </div>
                    )}
                  </PlayerHitFx>
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <StatBarRow
                    action={
                      <InlineItemAction
                        item={healItem}
                        itemLabel={t("home.itemLabelHeal")}
                        slot={autoConfig.heal}
                        onChangeSlot={setSlot("heal")}
                        onUse={() => healItem && consumeMut.mutate(healItem.id)}
                        pending={consumeMut.isPending}
                        disabledReason={m.hp >= m.hp_max ? t("home.hpFull") : null}
                      />
                    }
                  >
                    <StatBar label="HP" value={m.hp} max={m.hp_max} color="hp" />
                  </StatBarRow>
                  <div>
                    <button
                      type="button"
                      onClick={() => setStatsOpen((v) => !v)}
                      aria-expanded={statsOpen}
                      aria-controls="player-stats-panel"
                      className="flex w-full items-center justify-between gap-2 rounded-md border border-primary/20 bg-background/40 px-2 py-1.5 text-[11px] font-mono uppercase tracking-widest text-muted-foreground transition hover:border-primary/40 hover:text-primary min-h-9"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {statsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {t("home.statsLabel")}
                      </span>
                      {!statsOpen && (
                        <span className="truncate normal-case tracking-normal text-[10px] text-muted-foreground/80">
                          Lv {m.level} · XP {m.xp ?? 0}/{xpForLevel(m.level)}
                        </span>
                      )}
                    </button>
                    {statsOpen && (
                      <div id="player-stats-panel" className="mt-2 space-y-1">
                        <StatBarRow>
                          <StatBar label="XP" value={m.xp ?? 0} max={xpForLevel(m.level)} color="xp" />
                        </StatBarRow>
                      </div>
                    )}
                  </div>
                  {enemy && <PlayerAttackTimer lastTickIso={m.last_tick} hp={m.hp} />}
                  {(restingSec > 0 || buffSec > 0 || activeEffects.length > 0 || activeBonuses.live || activeBonuses.topForm || sparkAvailable) && (
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      {restingSec > 0 && (
                        <span className="inline-flex items-center gap-1 rounded border border-blue-400/40 bg-blue-500/10 px-1.5 py-0.5 text-blue-300">
                          <Moon className="h-3 w-3" /> {t("home.statusSleeping", { sec: restingSec })}
                        </span>
                      )}
                      {buffSec > 0 && (
                        <span className="inline-flex items-center gap-1 rounded border border-fuchsia-400/40 bg-fuchsia-500/10 px-1.5 py-0.5 text-fuchsia-300">
                          <Swords className="h-3 w-3" /> {t("home.statusAtkBuff", { sec: buffSec })}
                        </span>
                      )}
                      {activeEffects.map((ef) => (
                        <span key={ef.key} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 ${ef.cls}`}>
                          <Sparkles className="h-3 w-3" /> {ef.label} · {ef.sec}s
                        </span>
                      ))}
                      {activeBonuses.live && (
                        <span className="inline-flex items-center gap-1 rounded border border-cyan-400/40 bg-cyan-500/10 px-1.5 py-0.5 text-cyan-300">⚡ Live</span>
                      )}
                      {activeBonuses.topForm && (
                        <span className="inline-flex items-center gap-1 rounded border border-emerald-400/40 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300">{t("home.statusTopForm")}</span>
                      )}
                      {(() => {
                        const focusLeft = m.focus_battles_left ?? 0;
                        return focusLeft > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded border border-amber-400/40 bg-amber-500/10 px-1.5 py-0.5 text-amber-300">
                            <Brain className="h-3 w-3" /> {t("home.statusFocus", { count: focusLeft })}
                          </span>
                        ) : null;
                      })()}
                      {sparkLevelOk && (
                        <button
                          onClick={() => sparkMut.mutate()}
                          disabled={sparkMut.isPending || !sparkAvailable}
                          className="inline-flex items-center gap-1 rounded border border-emerald-400/60 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
                        >
                          <Sparkles className="h-3 w-3" />
                          {sparkRemainHr > 0
                            ? t("home.lifesparkCooldown", { hours: sparkRemainHr })
                            : m.hp >= m.hp_max
                              ? t("home.lifesparkReady")
                              : t("home.lifesparkActivate")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Spezialattacken — auf Mobile zwischen Gegner und eigenem Xenomon
                (order-3); auf sm+ bleibt die Panel-Karte an ihrer bisherigen Stelle
                unter der Arena. */}
            <div className="order-3 sm:order-none rounded-2xl border border-primary/30 bg-card/70 p-3 shadow-card backdrop-blur sm:p-4">
              <MovesPanel />
            </div>

            {/* Verbrauchsgegenstände */}
            <div className="order-5 sm:order-none rounded-2xl border border-primary/30 bg-card/70 p-3 shadow-card backdrop-blur sm:p-4">
              <ConsumablesQuickbar items={items} />
            </div>
          </section>

          {/* ─── Sidebar: Kampf-Log + Ereignisse ────────────────────── */}
          <aside className="flex min-w-0 flex-col gap-4">
            <CombatLog log={(combatLog ?? []) as { kind: string; text: string; detail?: string }[]} />
            <EventFeed
              title={t("home.eventsTitle")}
              allowedTypes={COMBAT_TYPES}
              filters={COMBAT_FILTERS}
            />
          </aside>
        </div>
      </main>
    </div>
  );
}
